// 50 discrete, evenly-spaced hues (360°/50 = 7.2° apart) for card-header
// tints. Kept as full hue coverage rather than a handful of named accents
// so there's real variety at this count — see cardHeaderHue() for why a
// single fixed saturation/lightness works safely at ANY hue.
const HUE_COUNT = 50;

// Deterministic per-card header hue — hashed from a code so each card gets
// a consistent look across renders/refetches instead of reshuffling every
// time (a literal Math.random() per render would flicker).
//
// Uses FNV-1a rather than a simple `h = h*31 + c` polynomial hash: 31 is
// congruent to 1 mod 5, which — back when this picked from 5 named
// accents — collapsed the hash into a plain sum-of-char-codes checksum,
// so position stopped mattering and similar-prefix course codes (e.g.
// "TMISTGPT-EN-2401"/"…-2402") clustered into the same bucket. FNV-1a's
// multiply+XOR avalanches properly regardless of the modulus.
export function cardHeaderHue(seed) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const bucket = Math.abs(hash) % HUE_COUNT;
  return Math.round((360 / HUE_COUNT) * bucket);
}

// WCAG note: the header background is `color-mix(in srgb, --cloud 62%,
// hsl(hue, 55%, 45%) 38%, --border-soft)` (see .cc-hd-tint in index.css).
// Fixing saturation/lightness at 55%/45% and only rotating hue keeps the
// mixed result's relative luminance in a bounded range regardless of hue —
// verified at both ends of the perceived-lightness spectrum: hue 60°
// (yellow, the brightest hue at a given HSL lightness) mixes to ~12.9:1
// (light) / ~8.1:1 (dark) against --ink, and hue ~210° (blue, the
// darkest-perceived) mixes to ~6.6:1 / ~7.0:1 — both comfortably clear the
// 4.5:1 minimum for the --ink title text in both themes. The small bold
// course-code label does NOT sit on this gradient — it lives in its own
// opaque .cc-code chip — because plain --slate text on ANY tinted variant
// of this header (even the old 5-accent palette at just 15% mix) dropped
// as low as 3.7:1, well under 4.5:1.
